/* eslint-disable react/no-did-update-set-state */

import React from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import Dropzone from 'react-dropzone';

import StyledModal, { ModalActions } from '../Modal';
import Button from '../Button';
import * as Icons from '../icons/Icons';
import SinglePhotoManager from './SinglePhotoManager';
import { generateImageName, getOrientation } from './utils/imageHelpers';

import { UPDATE } from './utils/UploadManager';
import { UPLOAD_MANAGER_SHAPE } from './shapes';
import { FlexCol, FlexRow } from '../FlexGrid';
import Heading from '../Heading';

export default class PhotoGalleryManager extends React.PureComponent {
    static propTypes = {
        gallery: PropTypes.array,
        saveToGallery: PropTypes.func,
        onPublish: PropTypes.func,
        onDelete: PropTypes.func,
        onReload: PropTypes.func,
        uploadManager: UPLOAD_MANAGER_SHAPE.isRequired,
        onHeroPromotion: PropTypes.func
    };

    static defaultProps = {
        gallery: [],
        onPublish: () => { },
        onDelete: () => { },
        onReload: () => { },
        saveToGallery: () => { },
        onHeroPromotion: () => { }
    };

    state = {
        isHelpModalOpen: false,
        dropzoneActive: false,
        error: '',
        gallery: this.props.gallery,
        unpublishedItems: []
    };

    componentDidUpdate(prevProps) {
        const { gallery } = this.props;

        if (gallery !== prevProps.gallery) {
            this.setState({
                gallery
            });
        }
    }

    onDragEnter = () => {
        this.setState({
            dropzoneActive: true
        });
    };

    onDragLeave = () => {
        this.setState({
            dropzoneActive: false
        });
    };

    onDropRejected = () => {
        // Currently ignoring a bad file drop - no warning is presented, but
        // also the UI doesn't break.
    };

    onDrop = (acceptedFiles) => {
        this.setState({
            dropzoneActive: false
        });

        acceptedFiles.forEach((file) => {
            // Prevent memory leaks
            window.URL.revokeObjectURL(file.preview);

            const reader = new FileReader();

            reader.onload = (e) => {
                const result = e.target.result;
                const img = new Image();

                img.onload = async () => {
                    const exifOrientation = await getOrientation(result);

                    const droppedImage = {
                        source: result,
                        name: generateImageName(file.name),
                        size: file.size,
                        height: img.height,
                        width: img.width,
                        exifOrientation
                    };

                    // If the dropped image has a long edge longer than 4000px,
                    // we're going to downsize it to avoid crazy big images
                    // from getting uploaded
                    //const originalImage = await optimizeImage(droppedImage);
                    // ^^-- this is buggy - exif rotations are not right at least.. plus not needed- backend takes care of image optimizations already
                    const originalImage = droppedImage;

                    const newItem = {
                        id:
                            Math.random()
                                .toString(36)
                                .substr(2, 9) + Date.now().toString(),
                        originalImage,
                        croppedImage: {
                            ...originalImage,
                            pixelCrop: {
                                x: 0,
                                y: 0,
                                width: originalImage.width,
                                height: originalImage.height
                            }
                        },
                        position: -1,
                        is_gallery: true,
                        is_preview: false,
                        is_primary: false,
                    };

                    this.setState({
                        unpublishedItems: [...this.state.unpublishedItems, newItem]
                    });
                };

                img.src = result;
            };

            if (file) {
                reader.readAsDataURL(file);
            }
        });
    };

    onPublish = (newItem) => {
        if (newItem) {
            const unpublishedItems = this.state.unpublishedItems.filter((item) => item.id !== newItem.id);

            this.setState({
                gallery: [...this.state.gallery, newItem],
                unpublishedItems
            });
        }
        this.props.onPublish()
    };

    onCancel = (canceledItem) => {
        const unpublishedItems = this.state.unpublishedItems.filter((item) => item.id !== canceledItem.id);

        this.setState({ unpublishedItems },()=>{
            this.props.onReload();
        });
    };

    onUpdate = () => {
    };

    getIsAltTextUnique = (input, id) => {
        const { gallery } = this.state;

        const galleryMinusThisOne = gallery.filter((item) => {
            return item.id !== id;
        });

        const index = galleryMinusThisOne.findIndex((item) => {
            return item.altText?.trim().toUpperCase() === input.trim().toUpperCase();
        });

        if (index === -1) {
            return true;
        }

        return false;
    }

    getMaxGaleryPosition = () => {
        const { gallery } = this.state;
        let maxGalleryPosition = 0;
        gallery.forEach((item) => {
            if (item.position > maxGalleryPosition) {
                maxGalleryPosition = item.position;
            }
        });
        if (maxGalleryPosition < (gallery.length - 1) && gallery.length > 0) {
            maxGalleryPosition = gallery.length - 1;
        }
        return maxGalleryPosition;
    }

    reorderGallery = ({ oldIndex, newIndex }) => {
        const movedItem = this.state.gallery.find((item, index) => index === oldIndex);
        const otherItems = this.state.gallery.filter((item, index) => index !== oldIndex);
        const verifiedNewIndex = newIndex > this.state.gallery.length ? this.state.gallery.length - 1 : newIndex;

        const gallery = [...otherItems.slice(0, verifiedNewIndex), movedItem, ...otherItems.slice(verifiedNewIndex)];

        this.setState({ gallery });

        // update every item that is at lowest index and higher
        gallery.forEach((item, index) => {
            if (item.position !== (index + 1)) {
                const image = {
                    credits: item.credit,
                    description: item.caption,
                    media_id: item.originalImage.mediaId,
                    title: item.altText,
                    url: item.croppedImage.source,
                    is_gallery: item.is_gallery,
                    is_primary: item.is_primary,
                    is_preview: item.is_preview
                };
                if (!image.is_gallery && !image.is_primary && !image.is_preview){
                    image.is_gallery = true;
                }
                image.position = index + 1;

                const task = {
                    type: UPDATE,
                    image,
                    onCompletion: () => {
                        this.props.onReload();
                    },
                    onError: (err) => {
                        // eslint-disable-next-line no-console
                        console.warn('Error updating position:', err);
                    }
                };
                this.props.uploadManager.addTask(task);
            }
        });
    };

    toggleHelpModal = () => {
        this.setState({ isHelpModalOpen: !this.state.isHelpModalOpen });
    };

    deleteGalleryItem = (id) => {
        const gallery = this.state.gallery.filter((item) => item.id !== id);

        this.setState({ gallery });
        this.props.onDelete();
    };

    renderGalleryItems = () => {
        const { gallery } = this.state;
        const { uploadManager, onHeroPromotion } = this.props;

        if (!gallery.length) {
            return null;
        }

        const maxGalleryPosition = this.getMaxGaleryPosition();

        return gallery.map((galleryItem, index) => {
            return (
                <SinglePhotoManager
                    {...galleryItem}
                    key={`gallery_${galleryItem.id}`}
                    id={galleryItem.id}
                    imageType={galleryItem.is_primary ? 'hero' : 'gallery'}
                    imageTypeDisplay="Gallery Image"
                    uploadManager={uploadManager}
                    onDelete={this.deleteGalleryItem}
                    onPublish={this.onPublish}
                    onUpdate={this.onUpdate}
                    position={index}
                    numberOfGalleryItems={gallery.length}
                    galleryPosition={galleryItem.position}
                    maxGalleryPosition={maxGalleryPosition}
                    reorderGallery={this.reorderGallery}
                    onHeroPromotion={onHeroPromotion}
                    getIsAltTextUnique={this.getIsAltTextUnique}
                    showCropOnSelect
                    isFreeformCrop />
            );
        });
    };

    renderUnpublishedItems = () => {
        const { unpublishedItems, gallery } = this.state;
        const { uploadManager } = this.props;

        if (!unpublishedItems.length) {
            return null;
        }

        const maxGalleryPosition = this.getMaxGaleryPosition();

        return unpublishedItems.map((galleryItem) => {
            return (
                <SinglePhotoManager
                    {...galleryItem}
                    key={`gallery_${galleryItem.id}`}
                    id={galleryItem.id}
                    imageType="gallery"
                    imageTypeDisplay="Gallery Image"
                    uploadManager={uploadManager}
                    onDelete={this.deleteGalleryItem}
                    onCancel={this.onCancel}
                    onPublish={this.onPublish}
                    showCropOnSelect
                    showCropOnMount
                    isFreeformCrop
                    numberOfGalleryItems={gallery.length}
                    maxGalleryPosition={maxGalleryPosition}
                    getIsAltTextUnique={this.getIsAltTextUnique} />
            );
        });
    };


    render() {
        const { dropzoneActive, unpublishedItems, isHelpModalOpen, error } = this.state;
        const hasUnsavedGalleryImages = unpublishedItems.length > 0;
        const hasError = error && error.length > 0;
        let buttonText = '';

        if (hasUnsavedGalleryImages) {
            buttonText = 'Save Changes Before Adding Another Gallery Image';
        }
        else {
            buttonText = this.state.gallery.length ? 'Add Another Gallery Image' : 'Add Gallery Image';
        }


        return (
            <div className="rec-photo-gallery-manager">
                <FlexRow alignItems="center" hasGutters={false} className='rec-photo-upload-page-section-heading'>
                    <FlexCol xs="variable">
                        <Heading headingLevel={2} appearance="h4">
                            Gallery Images
                        </Heading>
                    </FlexCol>
                    <FlexCol>
                        <Button
                            className="rec-photo-upload-page-section-helptip-trigger"
                            onClick={this.toggleHelpModal}
                            appearance="subtle"
                            size="sm"
                            iconBeforeElement={<Icons.IconInfo  />}
                            screenReaderTextBefore="Gallery Image Example" />
                    </FlexCol>
                </FlexRow>

                <div className="rec-photo-gallery-items-wrapper">
                    {this.renderGalleryItems()}
                    {this.renderUnpublishedItems()}

                    <FlexRow>
                        <FlexCol xl={10} xlOffset={1}>
                            <Dropzone
                                multiple
                                className="rec-photo-gallery-dropzone"
                                accept="image/jpeg, image/png"
                                onDrop={this.onDrop}
                                onDragEnter={this.onDragEnter}
                                onDropRejected={this.onDropRejected}
                                onDragLeave={this.onDragLeave}
                                inputProps={{
                                    id: `photo-files-input-gallery`,
                                    className: 'rec-photo-manager-files-input'
                                }}>
                                <div className="rec-photo-gallery-upload-control">
                                    <button className="rec-button-primary" disabled={hasUnsavedGalleryImages}>
                                        {buttonText}
                                    </button>
                                    <div className={cx('rec-photo-gallery-dropzone-overlay', { active: dropzoneActive })}>
                                        <div className="contents">
                                            <Icons.IconCloudUpload />
                                            <h3>Drop File to Upload Image to the Gallery</h3>
                                        </div>
                                    </div>
                                    <div className={cx('rec-photo-gallery-dropzone-overlay', { active: dropzoneActive || hasError })}>
                                        <div className="contents">
                                            <Icons.IconCloudUpload />
                                            <h3>Drop File to Upload Image to the Gallery</h3>
                                        </div>
                                    </div>
                                </div>
                            </Dropzone>
                        </FlexCol>
                    </FlexRow>
                </div>

                <StyledModal
                    heading="Gallery Images"
                    isOpen={isHelpModalOpen}
                    onRequestClose={this.toggleHelpModal}>
                    <div className="rec-photo-help-modal-content">
                        <p>
                            In addition to the hero and preview images, you&rsquo;re also able to upload other photos for use in an image gallery
                            section on the inventory page for your location. There is no limit on the number of images you can add to the gallery, so
                            feel free to include as many photos as you&rsquo;d like for your location.
                        </p>
                    </div>
                    <ModalActions>
                        <Button onClick={this.toggleHelpModal}>
                            Close
                        </Button>
                    </ModalActions>
                </StyledModal>
            </div>
        );
    }
}
